import { createRoot } from 'react-dom/client';
import { Button } from '@seeqdev/qomponents';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import type { JSX } from 'react';
import type { Root } from 'react-dom/client';

export type CreateFlatpickrFooterParameters = {
  flatpickrInstance: FlatpickrInstance;
};
export type CreateFlatpickrFooterReturn = (() => void) | null;
export type CreateFlatpickrFooter = <P extends CreateFlatpickrFooterParameters>(
  params: P,
) => CreateFlatpickrFooterReturn;

export const FOOTER_CSS_CLASS = 'flatpickr-footer';

export const hasFlatpickrFooter = (instance: FlatpickrInstance): boolean =>
  !!instance.calendarContainer.querySelector(`.${FOOTER_CSS_CLASS}`);

export const renderReactElementToDOM = (children: JSX.Element): [Root, HTMLDivElement] => {
  const el: HTMLDivElement = document.createElement('div');
  const root: Root = createRoot(el);
  root.render(children);
  return [root, el];
};

interface CreateFlatpickrFooterButtonsParameters extends CreateFlatpickrFooterParameters {
  t: (key: string) => string;
  onCancel: () => void;
  onDone: () => void;
}

export const createFooterButtons = ({
  flatpickrInstance: instance,
  t,
  onCancel,
  onDone,
}: CreateFlatpickrFooterButtonsParameters): CreateFlatpickrFooterReturn => {
  if (hasFlatpickrFooter(instance)) return null;

  const footer = document.createElement('div');
  footer.classList.add(FOOTER_CSS_CLASS);

  const [cancelRoot, cancelContainer] = renderReactElementToDOM(
    <Button onClick={onCancel} label={t('CANCEL')} size="xs" extraClassNames="flatpickr-cancel-button" />,
  );
  const [doneRoot, doneContainer] = renderReactElementToDOM(
    <Button
      onClick={onDone}
      label={t('DONE')}
      variant="theme"
      size="xs"
      extraClassNames="tw:ml-1 flatpickr-done-button"
    />,
  );

  footer.appendChild(cancelContainer);
  footer.appendChild(doneContainer);
  instance.calendarContainer.appendChild(footer);

  return () => {
    try {
      cancelRoot.unmount();
      doneRoot.unmount();
    } catch (error) {
      console.warn('Error unmounting flatpickr footer buttons:', error);
    }

    // Ensure DOM cleanup even if React unmount fails
    if (footer.parentNode) {
      footer.remove();
    }
  };
};
